import { Grid } from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	Stack,
	Tab,
	Tabs,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { ToastContent } from "../../../../shared/components";
import { useVForm, VForm, VTextField } from "../../../../shared/forms";
import { useToast } from "../../../../shared/hooks";
import { returnMessageErrorAsync } from "../../../../shared/utils/returnMessageError";
import { trackAsync } from "../../../../shared/utils/telemetry";
import { FollowUpMessageCard } from "./FollowUpMessageCard";
import { GetSinisterInternalFollowupNotesResponse } from "../../../../shared/services/api/sinister/SinisterInternalFollowUpService";
import { isToday, isYesterday, format } from "date-fns";

import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { FollowUpServices } from "./GenericFollowUpModalContent";

// Componente irmão (não substitui) de GenericFollowUpModalContent.tsx — criado à parte,
// sem tocar no componente original, para não afetar os 4 contextos que já o usam
// (client, sinister, canceledProposal, capitalization). Só acrescenta a aba "Interno"
// para contextos que precisam de comunicação interna entre a equipe, separada do
// FollowUp "Externo" padrão (que envia e-mail para os contatos do cliente).

export interface InternalFollowUpServices {
	getNotes: (id: string) => Promise<GetSinisterInternalFollowupNotesResponse[]>;
	addNote: (id: string, message: string) => Promise<GetSinisterInternalFollowupNotesResponse>;
}

interface LoggedUser {
	id: string;
	name: string;
	email: string;
	role: string;
}

interface Props {
	clientId: string;
	open: boolean;
	onClose: () => void;
	loggedUser: LoggedUser;
	externalServices: FollowUpServices;
	internalServices: InternalFollowUpServices;
	permissionSendExternal?: Boolean;
	permissionSendInternal?: Boolean;
	permissionAddContact?: Boolean;
	defaultTab?: "external" | "internal";
}

const addContactSchema = yup.object().shape({
	email: yup.string().email().required(),
});

const cleanMessageHtml = (html: string) => {
	return html
		.replace(/^(<p><br><\/p>\s*)+/, "")
		.replace(/(\s*<p><br><\/p>)+$/, "")
		.replace(/(<p><br><\/p>\s*){2,}/g, "<p><br></p>")
		.trim();
};

const isEmptyContent = (state: EditorState) => {
	const raw = convertToRaw(state.getCurrentContent());
	const html = draftToHtml(raw).trim();
	const cleaned = cleanMessageHtml(html);
	return !cleaned || cleaned === "<p></p>" || cleaned === "<p><br></p>" || cleaned.replace(/<[^>]+>/g, "").trim() === "";
};

const formatGroupLabel = (date: Date): string => {
	if (isToday(date)) return "Hoje";
	if (isYesterday(date)) return "Ontem";
	return format(date, "dd/MM/yyyy");
};

interface FollowUpPanelProps {
	clientId: string;
	loggedUser: LoggedUser;
	permissionSend?: Boolean;
	notesService: { getNotes: (id: string) => Promise<any[]>; addNote: (id: string, message: string) => Promise<any> };
	contacts?: {
		permissionAddContact?: Boolean;
		getContacts: (id: string) => Promise<any[]>;
		addContact: (id: string, email: string) => Promise<void>;
		removeContact: (contactId: string) => Promise<void>;
	};
	active: boolean;
	emptyLabel: string;
	panelKind: "internal" | "external";
}

// Painel de notas reutilizado pelas duas abas (Externo/Interno) — a aba Externo passa
// `contacts`, a Interna não (equipe interna não recebe notificação por e-mail).
const FollowUpPanel = ({ clientId, loggedUser, permissionSend, notesService, contacts, active, emptyLabel, panelKind }: FollowUpPanelProps) => {
	const [allNotes, setAllNotes] = useState<any[]>([]);
	const [visibleCount, setVisibleCount] = useState(10);
	const [contactList, setContactList] = useState<any[]>([]);
	const [loadingNotes, setLoadingNotes] = useState(false);
	const [loadingContacts, setLoadingContacts] = useState(false);
	const [loadingSend, setLoadingSend] = useState(false);
	const [search, setSearch] = useState("");
	const [editorState, setEditorState] = useState(EditorState.createEmpty());

	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const { formRef } = useVForm();
	const toast = useToast();
	const limit = 10;

	const isSendDisabled = loadingSend || isEmptyContent(editorState);
	// `permissionAddContact` não definido = liberado (comportamento histórico); só `false` bloqueia.
	const canAddContact = contacts?.permissionAddContact !== false;

	const isSearching = search.trim().length > 0;
	const hiddenNotesCount = Math.max(allNotes.length - visibleCount, 0);

	const showOlderNotes = () => {
		const container = scrollContainerRef.current;
		if (!container || hiddenNotesCount === 0) return;
		const prevHeight = container.scrollHeight;

		setVisibleCount(Math.min(visibleCount + limit, allNotes.length));

		requestAnimationFrame(() => {
			container.scrollTop = container.scrollHeight - prevHeight;
		});
	};

	useEffect(() => {
		const container = scrollContainerRef.current;
		const handleScroll = () => {
			if (container && container.scrollTop === 0 && !isSearching) showOlderNotes();
		};

		container?.addEventListener("scroll", handleScroll);
		return () => container?.removeEventListener("scroll", handleScroll);
	}, [allNotes, visibleCount, isSearching]);

	const fetchNotes = async () => {
		setLoadingNotes(true);
		try {
			const data = await notesService.getNotes(clientId);
			if (Array.isArray(data)) {
				const sorted = data.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
				setAllNotes(sorted);
				setVisibleCount(limit);

				setTimeout(() => {
					scrollRef.current?.scrollIntoView({ behavior: sorted.length > limit ? "auto" : "smooth" });
				}, 300);
			} else {
				setAllNotes([]);
			}
		} catch (e) {
			const { titleMessage, contentMessage } = await returnMessageErrorAsync(e);
			toast.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		} finally {
			setLoadingNotes(false);
		}
	};

	const handleSend = async () => {
		const rawContentState = convertToRaw(editorState.getCurrentContent());
		const htmlContent = draftToHtml(rawContentState).trim();
		const cleaned = cleanMessageHtml(htmlContent);
		const isEmpty = !cleaned || cleaned === "<p></p>" || cleaned === "<p><br></p>" || cleaned.replace(/<[^>]+>/g, "").trim() === "";
		if (isEmpty) return;
		setLoadingSend(true);
		try {
			const created = await trackAsync(`followup.${panelKind}_note`, () => notesService.addNote(clientId, cleaned), { sinisterId: clientId });
			setEditorState(EditorState.createEmpty());

			if (created && created.id && created.createdAt) {
				setAllNotes((prev) => [...prev, created]);
				setVisibleCount((prev) => prev + 1);
				setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
			} else {
				fetchNotes();
			}
		} catch (e) {
			const { titleMessage, contentMessage } = await returnMessageErrorAsync(e);
			toast.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		} finally {
			setLoadingSend(false);
		}
	};

	const fetchContacts = async () => {
		if (!contacts) return;
		setLoadingContacts(true);
		try {
			const data = await contacts.getContacts(clientId);
			setContactList(Array.isArray(data) ? data : []);
		} catch (e) {
			const { titleMessage, contentMessage } = await returnMessageErrorAsync(e);
			toast.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		} finally {
			setLoadingContacts(false);
		}
	};

	const addContact = async ({ email }: { email: string }) => {
		if (!contacts) return;
		try {
			await addContactSchema.validate({ email });
			await contacts.addContact(clientId, email);
			formRef.current?.setFieldValue("email", "");
			fetchContacts();
		} catch (e) {
			const { titleMessage, contentMessage } = await returnMessageErrorAsync(e);
			toast.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		}
	};

	const removeContact = async (id: string) => {
		if (!contacts) return;
		try {
			await contacts.removeContact(id);
			fetchContacts();
			toast.present({ type: "success", position: "top-right", messageOrContent: "Contato removido com sucesso" });
		} catch (e) {
			const { titleMessage, contentMessage } = await returnMessageErrorAsync(e);
			toast.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		}
	};

	useEffect(() => {
		if (active) {
			fetchNotes();
			fetchContacts();
		}
	}, [active]);

	const searchTerm = search.trim().toLowerCase();
	const matchingNotes = allNotes.filter((note) => note.note?.toLowerCase().includes(searchTerm) || note.userName?.toLowerCase().includes(searchTerm));
	const filteredNotes = isSearching ? matchingNotes : allNotes.slice(-visibleCount);

	return (
		<Box display="flex" flexDirection="column" height="100%">
			{contacts && (
				<Box sx={{ p: 2, borderBottom: "1px solid #eee" }}>
					<Accordion disableGutters>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>
							<Typography fontWeight={600} display="flex" alignItems="center" gap={1}>
								E-mail de Notificação {loadingContacts ? <CircularProgress size={14} /> : `(${contactList.length})`}
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							{canAddContact && (
								<VForm ref={formRef} onSubmit={addContact} onPointerEnterCapture={""} onPointerLeaveCapture={() => {}} placeholder={""}>
									<Grid container spacing={2} alignItems="center">
										<Grid item xs={10}>
											<VTextField fullWidth name="email" label="E-mail" size="small" />
										</Grid>
										<Grid item xs={2}>
											<Button fullWidth type="submit" variant="contained">
												Adicionar
											</Button>
										</Grid>
									</Grid>
								</VForm>
							)}
							<Box mt={2} display="flex" flexWrap="wrap" gap={1}>
								{contactList.map((contact) => (
									<Chip
										key={contact.id}
										label={contact.email}
										variant="outlined"
										color="default"
										onDelete={canAddContact ? () => removeContact(contact.id) : undefined}
										deleteIcon={<CloseIcon fontSize="small" />}
									/>
								))}
							</Box>
						</AccordionDetails>
					</Accordion>
				</Box>
			)}

			<Box sx={{ px: 2, py: 1, borderBottom: "1px solid #eee", bgcolor: "#fafafa" }}>
				<TextField
					fullWidth
					size="small"
					variant="outlined"
					placeholder="Buscar por nome ou conteúdo da mensagem..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" />
							</InputAdornment>
						),
						endAdornment: search && (
							<InputAdornment position="end">
								<IconButton size="small" onClick={() => setSearch("")}>
									<CloseIcon fontSize="small" />
								</IconButton>
							</InputAdornment>
						),
					}}
				/>
			</Box>

			<Box flex={1} overflow="auto" p={2} bgcolor="#f4f4f4" ref={scrollContainerRef}>
				{loadingNotes ? (
					<Box textAlign="center" mt={4}>
						<CircularProgress />
					</Box>
				) : filteredNotes.length === 0 ? (
					<Box textAlign="center" mt={4}>
						<Typography variant="body2" color="text.secondary">
							{isSearching && allNotes.length > 0 ? `Nenhuma nota encontrada entre as ${allNotes.length} notas` : emptyLabel}
						</Typography>
					</Box>
				) : (
					<Stack spacing={2}>
						{!isSearching && hiddenNotesCount > 0 && (
							<Box display="flex" justifyContent="center" alignItems="center" gap={1} flexWrap="wrap">
								<Typography variant="caption" color="text.secondary">
									Exibindo as últimas {filteredNotes.length} de {allNotes.length} notas
								</Typography>
								<Button size="small" variant="outlined" onClick={showOlderNotes}>
									Ver notas anteriores
								</Button>
							</Box>
						)}
						{isSearching && (
							<Typography variant="caption" color="text.secondary" textAlign="center">
								{matchingNotes.length} de {allNotes.length} notas encontradas
							</Typography>
						)}
						{filteredNotes.map((note, index) => {
							const currentDate = new Date(note.createdAt);
							const currentLabel = formatGroupLabel(currentDate);
							const prevNote = filteredNotes[index - 1];
							const prevDateLabel = prevNote ? formatGroupLabel(new Date(prevNote.createdAt)) : null;
							const showLabel = index === 0 || currentLabel !== prevDateLabel;

							return (
								<Box key={note.id}>
									{showLabel && (
										<Box display={"flex"} justifyContent={"center"}>
											<Typography
												variant="caption"
												sx={{
													textAlign: "center",
													display: "block",
													color: "text.secondary",
													fontWeight: 500,
													mt: index === 0 ? 0 : 2,
													mb: 1,
													bgcolor: "white",
													borderRadius: 5,
													justifyContent: "center",
													width: "auto",
													padding: "2px 10px",
													boxShadow: "rgba(17, 17, 26, 0.05) 0px 1px 0px, rgba(17, 17, 26, 0.1) 0px 0px 8px",
												}}
											>
												{currentLabel}
											</Typography>
										</Box>
									)}

									<FollowUpMessageCard note={note} isMine={note.userId === loggedUser.id} loggedUserName={loggedUser.name} />
								</Box>
							);
						})}
						<div ref={scrollRef} />
					</Stack>
				)}
			</Box>

			{permissionSend && (
				<Box sx={{ p: 2, borderTop: "1px solid #ddd", backgroundColor: "white" }}>
					<Editor
						editorState={editorState}
						onEditorStateChange={setEditorState}
						toolbar={{
							options: ["inline", "list", "blockType", "link"],
							inline: { options: ["bold", "italic", "underline"] },
							list: { options: ["unordered", "ordered"] },
							blockType: { options: ["Normal", "Blockquote", "Code"] },
						}}
						wrapperStyle={{ border: "1px solid #ccc", borderRadius: 4 }}
						editorStyle={{
							minHeight: 100,
							padding: 10,
							fontSize: "0.95rem",
							fontFamily: `'Inter', 'Roboto', 'Segoe UI', sans-serif`,
							lineHeight: 1.6,
						}}
					/>

					<Box display="flex" justifyContent="flex-end" mt={1}>
						<Button onClick={handleSend} variant="contained" disabled={isSendDisabled} endIcon={<SendIcon />}>
							Enviar
						</Button>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export const GenericFollowUpTabsModalContent = ({
	clientId,
	open,
	onClose,
	loggedUser,
	externalServices,
	internalServices,
	permissionSendExternal,
	permissionSendInternal = true,
	permissionAddContact,
	defaultTab = "external",
}: Props) => {
	const [tab, setTab] = useState<"external" | "internal">(defaultTab);

	useEffect(() => {
		if (open) setTab(defaultTab);
	}, [open, defaultTab]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>
				<Typography variant="h6" color="primary">
					FollowUp
				</Typography>
			</DialogTitle>

			<Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
				<Tab value="external" label="Externo" />
				<Tab value="internal" label="Interno" />
			</Tabs>

			<DialogContent sx={{ display: "flex", flexDirection: "column", height: "80vh", p: 0 }}>
				<Box display={tab === "external" ? "flex" : "none"} flexDirection="column" height="100%">
					<FollowUpPanel
						clientId={clientId}
						loggedUser={loggedUser}
						permissionSend={permissionSendExternal}
						notesService={externalServices}
						contacts={{
							permissionAddContact,
							getContacts: externalServices.getContacts,
							addContact: externalServices.addContact,
							removeContact: externalServices.removeContact,
						}}
						active={open && tab === "external"}
						emptyLabel="Sem notas externas."
						panelKind="external"
					/>
				</Box>

				<Box display={tab === "internal" ? "flex" : "none"} flexDirection="column" height="100%">
					<FollowUpPanel
						clientId={clientId}
						loggedUser={loggedUser}
						permissionSend={permissionSendInternal}
						notesService={internalServices}
						active={open && tab === "internal"}
						emptyLabel="Sem notas internas."
						panelKind="internal"
					/>
				</Box>
			</DialogContent>
		</Dialog>
	);
};
